import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

import { City } from "@/constants/const";
import { RankXPName } from "@/constants/rank";
import {
  createLobby,
  joinAsDriver,
  joinAsExplosiveExpert,
  joinAsTeamExpert,
  joinAsWeaponExpert,
  leaveLobby,
  cancelLobby,
  kickMember,
} from "@/helper/contractFunctions/MafiaOCJoin";
import { getIsInLobby, getNextLobbyTime, getLobbyDetail } from "@/helper/contractFunctions/MafiaOCLobby";
import {
  startLobby,
  finishLobby,
  claimLobbyReward,
  getLobbyNonceStatus,
} from "@/helper/contractFunctions/MafiaOCExecution";
import { SERVER_URL } from "@/config/config";

// API Response Types
export interface LobbyDetailItemDetail {
  id: number;
  category_id: number;
  type_id: number;
  category_name: string;
  type_name: string;
  chance1?: number;
  chance2?: number;
  inventory_worth?: number;
  [key: string]: any;
}

export interface LobbyDetailItem {
  id: number;
  user_id: number;
  address: string;
  item_id: number;
  city: number;
  removed: number;
  status: boolean;
  timestamp: number;
  equipped: boolean;
  itemDetail: LobbyDetailItemDetail;
  Map: any | null;
  Car: any | null;
  [key: string]: any;
}

export interface LobbyApiMember {
  id: number;
  lobbyId: number;
  userId: number;
  memberIndex: number;
  impactScore: number;
  deductedScore: number;
  itemIds: string[];
  cash: number;
  bullet: number;
  timestamp: number;
  MemberUser: {
    id: number;
    name: string;
    customAvatar: string | null;
    imageId: number;
    rankNumber?: number;
  };
}

export interface LobbyDetailMember extends LobbyApiMember {
  items?: LobbyDetailItem[];
}

export interface LobbyApiLeader {
  id: number;
  name: string;
  customAvatar: string | null;
  imageId: number;
  rankNumber?: number;
}
export interface LobbyDetail {
  id: number;
  leaderId: number;
  city: number;
  createdAt: number;
  expectedLevel: number;
  minRank: number;
  status: number;
  timestamp: number;
  isSuccess: boolean;
  failureType: number;
  carDamage: number;
  cashDeduction: number;
  bulletsDeduction: number;
  bgDeduction: number;
  newBodyguardItemId: number;
  newBodyguardCategoryId: number;
  newBodyguardTypeId: number;
  healthDeductionPoint: number;
  rewardSizePercents: number[];
  rewardTypeIds: number[];
  rewardAmounts: number[];
  storyTextIndex: number;
  currentRewardIndex?: number;
  isRewardClaimed?: boolean;
  Leader: LobbyApiLeader;
  Members: LobbyApiMember[] | LobbyDetailMember[];
}


// Component Types
export interface OrganizedCrimePartyRole {
  role: string;
  playerName: string | null;
  isAvailable: boolean;
}

export interface OrganizedCrimeParty {
  id: string;
  leader: string;
  roles: OrganizedCrimePartyRole[];
  created: number;
  minRank: string;
  expectation: string;
  location: string;
}

// Helper function to map API response to component format
const mapLobbyApiToParty = (lobby: LobbyDetail): OrganizedCrimeParty => {
  const roleNames = ["Leader", "Driver", "Weapon", "Explosive", "Team"];
  const expectationMap = ["Full asset", "Partial asset", "No requirement"];

  // Create roles array
  const roles: OrganizedCrimePartyRole[] = roleNames.map((roleName, roleIndex) => {
    // Leader is always filled from Leader object
    if (roleIndex === 0) {
      return {
        role: roleName,
        playerName: lobby.Leader.name,
        isAvailable: false,
      };
    }

    // Find member for this role index (memberIndex 1-4 correspond to Driver, Weapon, Explosive, Team)
    // Note: memberIndex 0 is the leader's record in Members table
    const member = lobby.Members.find((m) => m.memberIndex === roleIndex);

    if (member && member.MemberUser) {
      return {
        role: roleName,
        playerName: member.MemberUser.name,
        isAvailable: false,
      };
    }

    // Role is available if not filled
    return {
      role: roleName,
      playerName: null,
      isAvailable: true,
    };
  });

  return {
    id: lobby.id.toString(),
    leader: lobby.Leader.name,
    roles,
    created: lobby.createdAt,
    minRank: RankXPName[lobby.minRank] || "Unknown",
    expectation: expectationMap[lobby.expectedLevel] || "Unknown",
    location: City[lobby.city] || "Unknown",
  };
};

export interface OrganizedCrimeState {
  parties: OrganizedCrimeParty[];
  isLoadingParties: boolean;
  activePartyId: string | null;
  lobbyDetail: LobbyDetail | null;
  isLoadingLobbyDetail: boolean;
  policeReports: LobbyDetail[];
  isLoadingPoliceReports: boolean;
  isCreatingLobby: boolean;
  isJoiningDriver: boolean;
  isJoiningWeaponExpert: boolean;
  isJoiningExplosiveExpert: boolean;
  isJoiningTeamExpert: boolean;
  isLeavingLobby: boolean;
  isCancelingLobby: boolean;
  isKickingMember: boolean;
  isStartingLobby: boolean;
  isFinishingLobby: boolean;
  isClaimingReward: boolean;
  isLobbyReadyToFinish: boolean;
  isCheckingReadyToFinish: boolean;
  nextLobbyTime: number;
  isInLobby: boolean | null;
  error: string | null;
}

const initialState: OrganizedCrimeState = {
  parties: [],
  isLoadingParties: false,
  activePartyId: null,
  lobbyDetail: null,
  isLoadingLobbyDetail: false,
  policeReports: [],
  isLoadingPoliceReports: false,
  isCreatingLobby: false,
  isJoiningDriver: false,
  isJoiningWeaponExpert: false,
  isJoiningExplosiveExpert: false,
  isJoiningTeamExpert: false,
  isLeavingLobby: false,
  isCancelingLobby: false,
  isKickingMember: false,
  isStartingLobby: false,
  isFinishingLobby: false,
  isClaimingReward: false,
  isLobbyReadyToFinish: false,
  isCheckingReadyToFinish: false,
  nextLobbyTime: 0,
  isInLobby: null,
  error: null,
};

export const createLobbyAction = createAsyncThunk(
  "organizedCrime/createLobbyAction",
  async ({
    cashAmount,
    assetExpectation,
    minRank,
    message,
    signature,
    account,
  }: {
    cashAmount: number;
    assetExpectation: number;
    minRank: number;
    message: string;
    signature: string;
    account: string;
  }) => {
    const tx = await createLobby(
      cashAmount,
      assetExpectation,
      minRank,
      message,
      signature,
      account
    );
    return tx?.transactionHash || "";
  }
);

export const joinAsDriverAction = createAsyncThunk(
  "organizedCrime/joinAsDriverAction",
  async ({
    lobbyId,
    carId,
    message,
    signature,
    account,
  }: {
    lobbyId: number;
    carId: number;
    message: string;
    signature: string;
    account: string;
  }) => {
    const tx = await joinAsDriver(
      lobbyId,
      carId,
      message,
      signature,
      account
    );
    return tx?.transactionHash || "";
  }
);

export const joinAsWeaponExpertAction = createAsyncThunk(
  "organizedCrime/joinAsWeaponExpertAction",
  async ({
    lobbyId,
    weaponId,
    bulletAmount,
    message,
    signature,
    account,
  }: {
    lobbyId: number;
    weaponId: number;
    bulletAmount: number;
    message: string;
    signature: string;
    account: string;
  }) => {
    const tx = await joinAsWeaponExpert(
      lobbyId,
      weaponId,
      bulletAmount,
      message,
      signature,
      account
    );
    return tx?.transactionHash || "";
  }
);

export const joinAsExplosiveExpertAction = createAsyncThunk(
  "organizedCrime/joinAsExplosiveExpertAction",
  async ({
    lobbyId,
    grenadeIds,
    molotovIds,
    armorId,
    message,
    signature,
    account,
  }: {
    lobbyId: number;
    grenadeIds: number[];
    molotovIds: number[];
    armorId: number;
    message: string;
    signature: string;
    account: string;
  }) => {
    const tx = await joinAsExplosiveExpert(
      lobbyId,
      grenadeIds,
      molotovIds,
      armorId,
      message,
      signature,
      account
    );
    return tx?.transactionHash || "";
  }
);

export const joinAsTeamExpertAction = createAsyncThunk(
  "organizedCrime/joinAsTeamExpertAction",
  async ({
    lobbyId,
    bodyguardId,
    message,
    signature,
    account,
  }: {
    lobbyId: number;
    bodyguardId: number;
    message: string;
    signature: string;
    account: string;
  }) => {
    const tx = await joinAsTeamExpert(
      lobbyId,
      bodyguardId,
      message,
      signature,
      account
    );
    return tx?.transactionHash || "";
  }
);

export const getNextLobbyTimeAction = createAsyncThunk(
  "organizedCrime/getNextLobbyTimeAction",
  async (account: string) => {
    const nextLobbyTime = await getNextLobbyTime(account);
    return nextLobbyTime;
  }
);

export const getIsInLobbyAction = createAsyncThunk(
  "organizedCrime/getIsInLobbyAction",
  async (account: string) => {
    const isInLobby = await getIsInLobby(account);
    return isInLobby;
  }
);

export const getLobbyListAction = createAsyncThunk(
  "organizedCrime/getLobbyListAction",
  async ({
    from = 0,
    count = 10,
    status,
    city,
  }: {
    from?: number;
    count?: number;
    status?: number;
    city?: number;
  } = {}) => {
    const queryParams = new URLSearchParams({
      from: from.toString(),
      count: count.toString(),
    });

    if (status !== undefined) {
      queryParams.append("status", status.toString());
    }

    if (city !== undefined) {
      queryParams.append("city", city.toString());
    }

    const response = await axios.get<LobbyDetail[]>(
      `${SERVER_URL}/organized-crime/lobbies?${queryParams.toString()}`
    );

    return response.data.map(mapLobbyApiToParty);
  }
);

export const getPoliceReportsAction = createAsyncThunk(
  "organizedCrime/getPoliceReportsAction",
  async ({
    from = 0,
    count = 20,
    city,
  }: {
    from?: number;
    count?: number;
    city?: number;
  } = {}) => {
    const queryParams = new URLSearchParams({
      from: from.toString(),
      count: count.toString(),
      status: "2", // Finished status
    });

    if (city !== undefined) {
      queryParams.append("city", city.toString());
    }

    const response = await axios.get<LobbyDetail[]>(
      `${SERVER_URL}/organized-crime/lobbies?${queryParams.toString()}`
    );

    return response.data;
  }
);

export const getLobbyDetailAction = createAsyncThunk(
  "organizedCrime/getLobbyDetailAction",
  async (lobbyId: number) => {
    const response = await axios.get<LobbyDetail>(
      `${SERVER_URL}/organized-crime/lobbies/${lobbyId}`
    );

    // Fetch contract data to get currentRewardIndex and isRewardClaimed
    try {
      const contractData = await getLobbyDetail(lobbyId);
      return {
        ...response.data,
        currentRewardIndex: contractData.currentRewardIndex,
        isRewardClaimed: contractData.isRewardClaimed,
      };
    } catch (error) {
      // If contract call fails, return parsed lobby without contract data
      console.error("Failed to fetch contract data:", error);
      return response.data;
    }
  }
);

export const leaveLobbyAction = createAsyncThunk(
  "organizedCrime/leaveLobbyAction",
  async ({
    lobbyId,
    account,
  }: {
    lobbyId: number;
    account: string;
  }) => {
    const tx = await leaveLobby(lobbyId, account);
    return tx?.transactionHash || "";
  }
);

export const cancelLobbyAction = createAsyncThunk(
  "organizedCrime/cancelLobbyAction",
  async ({
    lobbyId,
    account,
  }: {
    lobbyId: number;
    account: string;
  }) => {
    const tx = await cancelLobby(lobbyId, account);
    return tx?.transactionHash || "";
  }
);

export const kickMemberAction = createAsyncThunk(
  "organizedCrime/kickMemberAction",
  async ({
    lobbyId,
    memberIndex,
    account,
  }: {
    lobbyId: number;
    memberIndex: number;
    account: string;
  }) => {
    const tx = await kickMember(lobbyId, memberIndex, account);
    return tx?.transactionHash || "";
  }
);

export const checkLobbyNonceStatusAction = createAsyncThunk(
  "organizedCrime/checkLobbyNonceStatusAction",
  async (lobbyId: number) => {
    const nonceStatus = await getLobbyNonceStatus(lobbyId);
    return Boolean(nonceStatus);
  }
);

export const startLobbyAction = createAsyncThunk(
  "organizedCrime/startLobbyAction",
  async ({
    lobbyId,
    account,
  }: {
    lobbyId: number;
    account: string;
  }) => {
    const tx = await startLobby(lobbyId, account);
    return { transactionHash: tx?.transactionHash || "", lobbyId };
  }
);

export const finishLobbyAction = createAsyncThunk(
  "organizedCrime/finishLobbyAction",
  async ({
    lobbyId,
    account,
  }: {
    lobbyId: number;
    account: string;
  }) => {
    const { txHash, blockNumber, lobbyFinishEvent } = await finishLobby(lobbyId, account);
    return {
      txHash,
      blockNumber,
      lobbyFinishEvent,
    };
  }
);

export const claimLobbyRewardAction = createAsyncThunk(
  "organizedCrime/claimLobbyRewardAction",
  async ({
    lobbyId,
    account,
  }: {
    lobbyId: number;
    account: string;
  }) => {
    const tx = await claimLobbyReward(lobbyId, account);
    return {
      txHash: tx?.transactionHash || "",
      lobbyId,
    };
  }
);

export const organizedCrimeSlice = createSlice({
  name: "organizedCrime",
  initialState,
  reducers: {
    setParties(state, action: PayloadAction<OrganizedCrimeParty[]>) {
      state.parties = action.payload;
    },
    setActivePartyId(state, action: PayloadAction<string | null>) {
      state.activePartyId = action.payload;
    },
    setIsLoadingParties(state, action: PayloadAction<boolean>) {
      state.isLoadingParties = action.payload;
    },
    setNextLobbyTime(state, action: PayloadAction<number>) {
      state.nextLobbyTime = action.payload;
    },
    setIsInLobby(state, action: PayloadAction<boolean | null>) {
      state.isInLobby = action.payload;
    },
    setOrganizedCrimeError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    clearOrganizedCrimeState() {
      return initialState;
    },
    updateLobbyDetail(state, action: PayloadAction<any>) {
      state.lobbyDetail = {
        ...state.lobbyDetail,
        ...action.payload,
      };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(createLobbyAction.pending, (state) => {
      state.isCreatingLobby = true;
    });
    builder.addCase(createLobbyAction.fulfilled, (state) => {
      state.isCreatingLobby = false;
    });
    builder.addCase(createLobbyAction.rejected, (state, action) => {
      state.isCreatingLobby = false;
      state.error = action.error.message || null;
    });

    builder.addCase(joinAsDriverAction.pending, (state) => {
      state.isJoiningDriver = true;
    });
    builder.addCase(joinAsDriverAction.fulfilled, (state) => {
      state.isJoiningDriver = false;
    });
    builder.addCase(joinAsDriverAction.rejected, (state, action) => {
      state.isJoiningDriver = false;
      state.error = action.error.message || null;
    });

    builder.addCase(joinAsWeaponExpertAction.pending, (state) => {
      state.isJoiningWeaponExpert = true;
    });
    builder.addCase(joinAsWeaponExpertAction.fulfilled, (state) => {
      state.isJoiningWeaponExpert = false;
    });
    builder.addCase(joinAsWeaponExpertAction.rejected, (state, action) => {
      state.isJoiningWeaponExpert = false;
      state.error = action.error.message || null;
    });

    builder.addCase(joinAsExplosiveExpertAction.pending, (state) => {
      state.isJoiningExplosiveExpert = true;
    });
    builder.addCase(joinAsExplosiveExpertAction.fulfilled, (state) => {
      state.isJoiningExplosiveExpert = false;
    });
    builder.addCase(joinAsExplosiveExpertAction.rejected, (state, action) => {
      state.isJoiningExplosiveExpert = false;
      state.error = action.error.message || null;
    });

    builder.addCase(joinAsTeamExpertAction.pending, (state) => {
      state.isJoiningTeamExpert = true;
    });
    builder.addCase(joinAsTeamExpertAction.fulfilled, (state) => {
      state.isJoiningTeamExpert = false;
    });
    builder.addCase(joinAsTeamExpertAction.rejected, (state, action) => {
      state.isJoiningTeamExpert = false;
      state.error = action.error.message || null;
    });

    builder.addCase(getNextLobbyTimeAction.fulfilled, (state, action) => {
      state.nextLobbyTime = action.payload;
    });

    builder.addCase(getIsInLobbyAction.fulfilled, (state, action) => {
      state.isInLobby = action.payload;
    });

    builder.addCase(getLobbyListAction.pending, (state) => {
      state.isLoadingParties = true;
    });
    builder.addCase(getLobbyListAction.fulfilled, (state, action) => {
      state.isLoadingParties = false;
      state.parties = action.payload;
    });
    builder.addCase(getLobbyListAction.rejected, (state, action) => {
      state.isLoadingParties = false;
      state.error = action.error.message || null;
    });

    builder.addCase(getPoliceReportsAction.pending, (state) => {
      state.isLoadingPoliceReports = true;
    });
    builder.addCase(getPoliceReportsAction.fulfilled, (state, action) => {
      state.isLoadingPoliceReports = false;
      state.policeReports = action.payload;
    });
    builder.addCase(getPoliceReportsAction.rejected, (state, action) => {
      state.isLoadingPoliceReports = false;
      state.error = action.error.message || null;
    });

    builder.addCase(getLobbyDetailAction.pending, (state) => {
      state.isLoadingLobbyDetail = true;
    });
    builder.addCase(getLobbyDetailAction.fulfilled, (state, action) => {
      state.isLoadingLobbyDetail = false;
      state.lobbyDetail = action.payload;
    });
    builder.addCase(getLobbyDetailAction.rejected, (state, action) => {
      state.isLoadingLobbyDetail = false;
      state.error = action.error.message || null;
    });

    builder.addCase(leaveLobbyAction.pending, (state) => {
      state.isLeavingLobby = true;
    });
    builder.addCase(leaveLobbyAction.fulfilled, (state) => {
      state.isLeavingLobby = false;
    });
    builder.addCase(leaveLobbyAction.rejected, (state, action) => {
      state.isLeavingLobby = false;
      state.error = action.error.message || null;
    });

    builder.addCase(cancelLobbyAction.pending, (state) => {
      state.isCancelingLobby = true;
    });
    builder.addCase(cancelLobbyAction.fulfilled, (state) => {
      state.isCancelingLobby = false;
    });
    builder.addCase(cancelLobbyAction.rejected, (state, action) => {
      state.isCancelingLobby = false;
      state.error = action.error.message || null;
    });

    builder.addCase(kickMemberAction.pending, (state) => {
      state.isKickingMember = true;
    });
    builder.addCase(kickMemberAction.fulfilled, (state) => {
      state.isKickingMember = false;
    });
    builder.addCase(kickMemberAction.rejected, (state, action) => {
      state.isKickingMember = false;
      state.error = action.error.message || null;
    });

    builder.addCase(checkLobbyNonceStatusAction.pending, (state) => {
      state.isCheckingReadyToFinish = true;
    });
    builder.addCase(checkLobbyNonceStatusAction.fulfilled, (state, action) => {
      state.isCheckingReadyToFinish = false;
      state.isLobbyReadyToFinish = action.payload;

      console.log("action.payload", action.payload);
      console.log("state.isLobbyReadyToFinish", state.isLobbyReadyToFinish);
    });
    builder.addCase(checkLobbyNonceStatusAction.rejected, (state, action) => {
      state.isCheckingReadyToFinish = false;
      state.error = action.error.message || null;

      console.log("action.error.message", action.error);
    });

    builder.addCase(startLobbyAction.pending, (state) => {
      state.isStartingLobby = true;
      state.isLobbyReadyToFinish = false;
    });
    builder.addCase(startLobbyAction.fulfilled, (state) => {
      state.isStartingLobby = false;
    });
    builder.addCase(startLobbyAction.rejected, (state, action) => {
      state.isStartingLobby = false;
      state.error = action.error.message || null;
    });

    builder.addCase(finishLobbyAction.pending, (state) => {
      state.isFinishingLobby = true;
    });
    builder.addCase(finishLobbyAction.fulfilled, (state) => {
      state.isFinishingLobby = false;
    });
    builder.addCase(finishLobbyAction.rejected, (state, action) => {
      state.isFinishingLobby = false;
      state.error = action.error.message || null;
    });

    builder.addCase(claimLobbyRewardAction.pending, (state) => {
      state.isClaimingReward = true;
    });
    builder.addCase(claimLobbyRewardAction.fulfilled, (state) => {
      state.isClaimingReward = false;
    });
    builder.addCase(claimLobbyRewardAction.rejected, (state, action) => {
      state.isClaimingReward = false;
      state.error = action.error.message || null;
    });
  },
});

export const {
  updateLobbyDetail,
  setParties,
  setActivePartyId,
  setIsLoadingParties,
  setNextLobbyTime,
  setIsInLobby,
  setOrganizedCrimeError,
  clearOrganizedCrimeState,
} = organizedCrimeSlice.actions;

export default organizedCrimeSlice.reducer;
